/**
 * VB Server - Groups API
 *
 * CRUD operations for organization groups (criteria-based).
 * Replaces the old channel-groups API with multi-criteria matching.
 *
 * @example
 * import { listGroups, createGroup } from '@/api/server/groups';
 *
 * const groups = await listGroups(orgId);
 * const newGroup = await createGroup(orgId, { name: 'Fire Dispatch', criteria: { channels: ['ch1'] } });
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';

// =============================================================================
// Types
// =============================================================================

/** Criteria shape: OR within each key, AND across keys. All keys optional. */
export interface GroupCriteria {
  channels?: string[];
  talkgroup_tags?: string[];
  radio_ids?: string[];
  speakers?: string[];
  tags?: string[];
}

/**
 * Group entity from vb-server
 */
export interface Group {
  group_id: string;
  org_id: string;
  name: string;
  color?: string | null;
  sort_order: number;
  criteria: GroupCriteria;
  status: number;
  created: string;
  created_by?: string | null;
  modified: string;
}

/**
 * Input for creating a new group
 */
export interface CreateGroupInput {
  name: string;
  color?: string | null;
  criteria: GroupCriteria;
}

/**
 * Input for updating a group
 */
export interface UpdateGroupInput {
  name?: string;
  color?: string | null;
  sort_order?: number;
  criteria?: GroupCriteria;
}

/**
 * Ungrouped value (channel, talkgroup tag, etc. not assigned to any group)
 */
export interface UngroupedValue {
  id: string;
  name: string;
  count?: number;
}

/**
 * Filter visibility map: field_id → visible boolean
 * Missing keys default to true, except source_type which defaults to false.
 */
export interface FilterVisibility {
  channel_id?: boolean;
  source_type?: boolean;
  device_id?: boolean;
  talkgroup_tag?: boolean;
  radio_id?: boolean;
  speaker_id?: boolean;
  tag?: boolean;
}

/**
 * Filter settings for the organization (includes group settings)
 */
export interface FilterSettings {
  default_group_type: string;
  filter_visibility: FilterVisibility;
}

export const FILTER_FIELDS: (keyof FilterVisibility)[] = [
  'channel_id',
  'source_type',
  'device_id',
  'talkgroup_tag',
  'radio_id',
  'speaker_id',
  'tag'
];

/** @deprecated Use FilterSettings */
export type GroupSettings = FilterSettings;

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all groups for an organization
 */
export async function listGroups(orgId: string): Promise<Group[]> {
  return serverGet<Group[]>(`/orgs/${orgId}/groups`);
}

/**
 * Create a new group
 */
export async function createGroup(orgId: string, data: CreateGroupInput): Promise<Group> {
  return serverPost<Group>(`/orgs/${orgId}/groups`, data);
}

/**
 * Update an existing group
 */
export async function updateGroup(
  orgId: string,
  groupId: string,
  data: UpdateGroupInput
): Promise<Group> {
  return serverPut<Group>(`/orgs/${orgId}/groups/${groupId}`, data);
}

/**
 * Delete a group (soft delete — criteria members become ungrouped)
 */
export async function deleteGroup(orgId: string, groupId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/groups/${groupId}`);
}

/**
 * Batch reorder groups
 */
export async function reorderGroups(
  orgId: string,
  order: Array<{ group_id: string; sort_order: number }>
): Promise<Group[]> {
  return serverPut<Group[]>(`/orgs/${orgId}/groups/reorder`, { order });
}

/**
 * Get ungrouped values for the organization (items not assigned to any group)
 */
export async function getUngroupedValues(orgId: string): Promise<UngroupedValue[]> {
  return serverGet<UngroupedValue[]>(`/orgs/${orgId}/groups/ungrouped`);
}

/**
 * Get filter settings for the organization
 */
export async function getFilterSettings(orgId: string): Promise<FilterSettings> {
  return serverGet<FilterSettings>(`/orgs/${orgId}/filter-settings`);
}

/**
 * Update filter settings for the organization
 */
export async function updateFilterSettings(
  orgId: string,
  settings: { default_group_type?: string; filter_visibility?: FilterVisibility }
): Promise<FilterSettings> {
  return serverPut<FilterSettings>(`/orgs/${orgId}/filter-settings`, settings);
}

/** @deprecated Use getFilterSettings */
export const getGroupSettings = getFilterSettings;

/** @deprecated Use updateFilterSettings */
export const updateGroupSettings = updateFilterSettings;
